// Resync: comparing a run's stored lines with what AxisCare says now.
//
// Moved out of pages/finance/BillingRun.js, where it ran untested inside the
// component. No Supabase here — loading the lines and the visits is the
// caller's job (lib/billing/runs.js applies the result).

import { normName, serviceDateOf, lineHours } from './claimLines.js';

/**
 * Sort each visit into 'new' (no line yet), 'updated' (a line whose hours or
 * rate moved) or 'existing' (a line that still agrees).
 *
 * A visit finds its line by AxisCare visit id first. A line stored without a
 * visit id — every Monday-run line until 19 October 2026 — falls back to client
 * name and service date, and each such line can be claimed by one visit only.
 *
 * @param {object[]} p.visits         billable visits, already filtered to the run's destination
 * @param {object[]} p.existingLines  the run's claim_lines
 * @param {string}   p.basis          the run's hours basis — never the wizard's current choice
 * @returns {{ visit, status, existingLine }[]}
 */
export const classifyResync = ({ visits = [], existingLines = [], basis }) => {
  // Index existing lines by visitId and by name+date for fallback matching
  const byVisitId = {};
  const byNameDate = {};
  const usedByNameDate = new Set();
  for (const l of existingLines) {
    if (l.axiscare_visit_id) byVisitId[l.axiscare_visit_id] = l;
    const nd = `${normName(l.client_name)}|${l.service_date}`;
    if (!byNameDate[nd]) byNameDate[nd] = [];
    byNameDate[nd].push(l);
  }

  return visits.map(v => {
    const visitId = String(v.visitId || '');
    const date = serviceDateOf(v);
    const clientName = v.client ? `${v.client.firstName || ''} ${v.client.lastName || ''}`.trim() : '';
    // Match by visitId first; fall back to name+date for lines without a stored visitId
    let existing = visitId ? byVisitId[visitId] : null;
    if (!existing && date && clientName) {
      const nd = `${normName(clientName)}|${date}`;
      const candidates = (byNameDate[nd] || []).filter(l => !l.axiscare_visit_id && !usedByNameDate.has(l.id));
      if (candidates.length) { existing = candidates[0]; usedByNameDate.add(existing.id); }
    }
    const h = lineHours(v, basis);
    let status = 'new';
    if (existing) {
      const hoursChanged = Math.abs((existing.hours || 0) - h) > 0.01;
      const rateChanged  = Math.abs((existing.rate || 0) - (v.chargeRate || 0)) > 0.01;
      status = (hoursChanged || rateChanged) ? 'updated' : 'existing';
    }
    return { visit: v, status, existingLine: existing || null };
  });
};

/**
 * The columns a resync writes onto a line that changed. An update changes
 * what the visit is worth, never who it is for or the authorisation it was
 * already given.
 *
 * @param {object} row           the line buildClaimLine() made for the visit now
 * @param {object} existingLine  the stored line
 * @param {string} now           ISO timestamp
 */
export const resyncLineUpdate = (row, existingLine, now) => ({
  service_code: row.service_code, modifier: row.modifier, service_label: row.service_label,
  source: row.source, caera_visit_id: row.caera_visit_id,
  hours: row.hours, scheduled_hours: row.scheduled_hours, clocked_hours: row.clocked_hours,
  hours_basis: row.hours_basis, units: row.units,
  rate: row.rate, accrued_amount: row.accrued_amount, charge_amount: row.charge_amount,
  gross_charge_amount: row.gross_charge_amount,
  medicaid_id: row.medicaid_id || existingLine.medicaid_id || '',
  axiscare_visit_id: row.axiscare_visit_id,
  updated_at: now,
  notes: (existingLine.notes || '') + ' [hours updated by resync]',
});
